import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';

const norm = (s) => (s == null ? '' : String(s)).toLowerCase();

/** z-index acima do FormModalLayout (z-50) e de headers fixos */
const DROPDOWN_Z = 10050;

/**
 * Campo de busca com lista filtrada — guarda o `id` da entidade selecionada.
 * Lista renderizada via portal (fixed) para não ser cortada por overflow em modais/páginas.
 * `options`: [{ id, label }]
 */
const EntityAutocomplete = ({
  id,
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Digite para buscar…',
  required = false,
  disabled = false,
  emptyMessage = 'Nenhum resultado',
  maxVisible = 80,
  className = '',
  /** Abre um fluxo externo (ex.: modal de cadastro) e fecha a lista. */
  onAddNew = undefined,
  addNewLabel = 'Adicionar novo…',
  /** Mantém o + clicável quando o campo de busca está desabilitado. */
  keepAddEnabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  const selectedLabel = useMemo(() => {
    const hit = options.find((o) => o.id === value);
    return hit ? hit.label : '';
  }, [options, value]);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel, value]);

  const filtered = useMemo(() => {
    const q = norm(query).trim();
    let list = options;
    if (q) {
      list = options.filter(
        (o) => norm(o.label).includes(q) || norm(o.id).includes(q)
      );
    }
    return list.slice(0, maxVisible);
  }, [options, query, maxVisible]);

  const positionMenu = useCallback(() => {
    if (!open || disabled || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const gap = 4;
    const below = r.bottom + gap;
    const spaceBelow = window.innerHeight - below - 12;
    const maxH = Math.min(224, Math.max(120, spaceBelow));
    setMenuStyle({
      position: 'fixed',
      top: below,
      left: r.left,
      width: Math.max(r.width, 220),
      maxHeight: maxH,
      zIndex: DROPDOWN_Z,
    });
  }, [open, disabled]);

  useLayoutEffect(() => {
    positionMenu();
  }, [positionMenu, filtered.length, query, open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => positionMenu();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, positionMenu]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpen(false);
      setQuery(selectedLabel);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [selectedLabel]);

  const pick = (opt) => {
    onChange(opt.id);
    setQuery(opt.label);
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const runAddNew = () => {
    setOpen(false);
    setQuery(selectedLabel);
    onAddNew();
  };

  const dropdown =
    open && !disabled ? (
      <ul
        ref={dropdownRef}
        style={menuStyle}
        className="overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
        role="listbox"
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</li>
        ) : (
          filtered.map((opt) => (
            <li key={opt.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === opt.id}
                className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt)}
              >
                <span className="font-medium text-gray-900">{opt.label}</span>
                {opt.id != null && String(opt.id).length > 0 ? (
                  <span className="ml-2 font-mono text-xs text-gray-400">
                    {String(opt.id).slice(0, 8)}…
                  </span>
                ) : null}
              </button>
            </li>
          ))
        )}
        {onAddNew ? (
          <li className="border-t border-gray-200 bg-primary-50/50">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-primary-800 hover:bg-primary-100/80 focus:outline-none"
              onMouseDown={(e) => e.preventDefault()}
              onClick={runAddNew}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              {addNewLabel}
            </button>
          </li>
        ) : null}
      </ul>
    ) : null;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label ? (
        <label htmlFor={id} className={`form-label ${required ? 'form-label-required' : ''}`}>
          {label}
        </label>
      ) : null}
      <div ref={anchorRef} className="flex gap-2">
        <input
          id={id}
          type="text"
          autoComplete="off"
          disabled={disabled}
          className="form-input min-w-0 flex-1"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            setOpen(true);
            if (v === '') onChange('');
          }}
          onFocus={() => setOpen(true)}
        />
        {onAddNew ? (
          <button
            type="button"
            className="btn btn-outline inline-flex shrink-0 items-center gap-1.5 border-primary-200 px-2.5 text-primary-800 hover:border-primary-400 hover:bg-primary-50"
            disabled={disabled && !keepAddEnabled}
            onClick={runAddNew}
            title={addNewLabel}
            aria-label={addNewLabel}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Novo</span>
          </button>
        ) : null}
        {value ? (
          <button
            type="button"
            className="btn btn-outline shrink-0 px-3"
            disabled={disabled}
            onClick={clear}
            title="Limpar"
          >
            ×
          </button>
        ) : null}
      </div>
      {typeof document !== 'undefined' && dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
};

export default EntityAutocomplete;
